import { FC, FormEvent, useCallback, useEffect, useState } from 'react';

import { Box, TextField } from '@mui/material';

import { useQuery } from 'react-query';

import { UpdateCompanyOperationalNotePayload, useOperationalNote } from '@hooks_iqfu/common/useOperationalNote';

import DatePickerWrapper from '@components_iqfu/dashboard/date_picker_wrapper/DatePickerWrapper';

import { useNotificationContext } from '@contexts_common/notificationContext';

import styles from './RightColumnContactInfo.module.scss';

type Props = {
  targetId: string;
};

const RightColumnContactInfo: FC<Props> = ({ targetId }) => {
  const { getCompanyOperationalNote, updateCompanyOperationalNote } = useOperationalNote();
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();

  const [isEditing, setIsEditing] = useState(false);

  const [contactDates, setContactDates] = useState<UpdateCompanyOperationalNotePayload>({
    next_contact_date: undefined,
    next_contact_admin_user: undefined,
    last_contact_date: undefined,
    last_contact_admin_user: undefined,
  });

  const { data: fetchedData, refetch } = useQuery(
    ['getCompanyOperationalNote', targetId],
    () => getCompanyOperationalNote({ companyId: targetId }),
    {
      enabled: targetId !== 'undefined' && !!targetId,
      staleTime: 600,
      refetchOnWindowFocus: false,
    },
  );

  const init = useCallback(() => {
    if (!fetchedData) return;
    setContactDates({
      next_contact_date: fetchedData.next_contact_date,
      next_contact_admin_user: fetchedData.next_contact_admin_user,
      last_contact_date: fetchedData.last_contact_date,
      last_contact_admin_user: fetchedData.last_contact_admin_user,
    });
  }, [fetchedData]);

  useEffect(() => {
    init();
  }, [fetchedData]);

  useEffect(() => {
    setIsEditing(false);
  }, [targetId]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!fetchedData) return;

    const result = await updateCompanyOperationalNote({
      companyId: targetId,
      comment: fetchedData.comment,
      adminSalesRepresentative: fetchedData.admin_sales_representative,
      contactDates,
    });

    if (!result.success) {
      setNotificationDisplay(true);
      setNotificationMessage('更新に失敗しました');
      setNotificationStatus('alert');
      return;
    }
    setIsEditing(false);
    setNotificationDisplay(true);
    setNotificationMessage('更新しました');
    setNotificationStatus('success');
    refetch();
  };

  return (
    <form onSubmit={handleSubmit} className={styles.right_column_container}>
      <div className={styles.right_column_contact_info_area}>
        <p className={styles.right_column_contact_info_area_title}>連絡日</p>
        {!isEditing && (
          <a
            className={styles.right_column_date_edit}
            onClick={() => {
              setIsEditing(true);
            }}
          >
            編集
          </a>
        )}
      </div>

      <div className={styles.right_column_date_wrapper}>
        {isEditing ? (
          <div className={styles.right_column_contact_dates_inputs}>
            <div>
              <p>前回連絡日</p>
              <DatePickerWrapper
                className={styles.right_column_input}
                state={contactDates.last_contact_date}
                setState={(new_value: string) => setContactDates((prev) => ({ ...prev, last_contact_date: new_value }))}
                inputType="day"
                outputType="day"
                size="width1"
              />
              <div className={styles.right_column_input_wrapper}>
                <p>過去連絡の担当者</p>
                <TextField
                  className={styles.right_column_input}
                  value={contactDates.last_contact_admin_user}
                  onChange={(event) =>
                    setContactDates((prev) => ({ ...prev, last_contact_admin_user: event.target.value }))
                  }
                />
              </div>
              <div className={styles.right_column_input_wrapper}>
                <p>次回連絡日</p>
                <DatePickerWrapper
                  className={styles.right_column_input}
                  state={contactDates.next_contact_date}
                  setState={(new_value: string) =>
                    setContactDates((prev) => ({ ...prev, next_contact_date: new_value }))
                  }
                  inputType="day"
                  outputType="day"
                  size="width1"
                />
              </div>
              <div className={styles.right_column_input_wrapper}>
                <p>次回連絡の担当者</p>
                <TextField
                  className={styles.right_column_input}
                  value={contactDates.next_contact_admin_user}
                  onChange={(event) =>
                    setContactDates((prev) => ({ ...prev, next_contact_admin_user: event.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.right_column_date}>
            {(contactDates.last_contact_date || contactDates.last_contact_admin_user) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <p> (前) {contactDates.last_contact_date}</p>
                <p>{contactDates.last_contact_admin_user?.slice(0, 3)}</p>
              </Box>
            )}
            {(contactDates.next_contact_date || contactDates.next_contact_admin_user) && (
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <p> (後) {contactDates.next_contact_date}</p>
                <p>{contactDates.next_contact_admin_user?.slice(0, 3)}</p>
              </Box>
            )}
          </div>
        )}
      </div>

      {isEditing && (
        <div className={styles.right_column_buttons}>
          <button
            type="button"
            className={styles.delete_button}
            onClick={() => {
              setIsEditing(false);
              init();
            }}
          >
            キャンセル
          </button>
          <button type="submit" className={styles.submit_button}>
            保存
          </button>
        </div>
      )}
    </form>
  );
};

export default RightColumnContactInfo;
